import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderIcon,
  FolderPlusIcon,
  HeartIcon,
  PencilSquareIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import VocabularySensePickerModal from '../components/VocabularySensePickerModal';
import VocabularyActionDialog from '../components/VocabularyActionDialog';
import { useI18n } from '../i18n/I18nProvider';

const PERSONAL_LIST_PAGE_SIZE = 12;

const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const requestJson = async (url, options = {}) => {
  const response = await fetch(url, { ...options, headers: { ...headers(), ...(options.headers || {}) } });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.message || 'Request failed.');
  return payload;
};

const stateStyles = {
  mastered: 'bg-emerald-50 text-emerald-800',
  review_due: 'bg-amber-50 text-amber-900',
  learning: 'bg-blue-50 text-blue-800',
  not_started: 'bg-gray-100 text-gray-700',
};

const stateLabels = {
  mastered: 'Mastered',
  review_due: 'Review due',
  learning: 'Learning',
  not_started: 'Not started',
};

const StudentWordbooksPage = () => {
  const { t } = useI18n();
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [lists, setLists] = useState([]);
  const [listPage, setListPage] = useState(1);
  const [listSearch, setListSearch] = useState('');
  const [listPagination, setListPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [selectedListId, setSelectedListId] = useState('');
  const [listDetails, setListDetails] = useState(null);
  const [settings, setSettings] = useState({ default_capture_list_id: '', enabled_personal_list_ids: [] });
  const [picker, setPicker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionDialog, setActionDialog] = useState(null);

  const loadFolders = useCallback(async ({ preserveSelection = true } = {}) => {
    const payload = await requestJson('/api/vocabulary/folders?scope=mine&ensure_defaults=1');
    const next = Array.isArray(payload) ? payload : [];
    setFolders(next);
    setSelectedFolderId((current) => (
      preserveSelection && next.some((folder) => folder._id === current)
        ? current
        : (next[0]?._id || '')
    ));
    return next;
  }, []);

  const loadSettings = useCallback(async () => {
    setSettings(await requestJson('/api/vocabulary/settings'));
  }, []);

  const loadLists = useCallback(async (folderId, preferredListId = '', requestedPage = 1, search = '') => {
    if (!folderId) {
      setLists([]);
      setSelectedListId('');
      setListPagination({ page: 1, pages: 1, total: 0 });
      return [];
    }
    const query = new URLSearchParams({
      page: String(requestedPage),
      page_size: String(PERSONAL_LIST_PAGE_SIZE),
    });
    if (search.trim()) query.set('search', search.trim());
    const payload = await requestJson(`/api/vocabulary/folders/${folderId}/lists?${query.toString()}`);
    const next = Array.isArray(payload) ? payload : (Array.isArray(payload.items) ? payload.items : []);
    setLists(next);
    setListPagination({
      page: Number(payload.page || requestedPage || 1),
      pages: Number(payload.pages || 1),
      total: Number(payload.total ?? next.length),
    });
    setSelectedListId((current) => {
      const wanted = preferredListId || current;
      return next.some((list) => list._id === wanted) ? wanted : (next[0]?._id || '');
    });
    return next;
  }, []);

  const loadDetails = useCallback(async (listId) => {
    if (!listId) {
      setListDetails(null);
      return;
    }
    setDetailLoading(true);
    try {
      setListDetails(await requestJson(`/api/vocabulary/lists/${listId}`));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadFolders(), loadSettings()]);
    } catch (loadError) {
      setError(loadError.message || t('Could not load your lists.'));
    } finally {
      setLoading(false);
    }
  }, [loadFolders, loadSettings, t]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  useEffect(() => {
    setError('');
    loadLists(selectedFolderId, '', listPage, listSearch).catch((loadError) => setError(loadError.message));
  }, [selectedFolderId, listPage, listSearch, loadLists]);

  useEffect(() => {
    loadDetails(selectedListId).catch((loadError) => setError(loadError.message));
  }, [selectedListId, loadDetails]);

  const selectedFolder = useMemo(
    () => folders.find((folder) => folder._id === selectedFolderId),
    [folders, selectedFolderId],
  );

  const saveSettings = async (updates) => {
    setSettings(await requestJson('/api/vocabulary/settings', {
      method: 'PUT',
      body: JSON.stringify(updates),
    }));
  };

  const createFolder = () => {
    setActionDialog({
      kind: 'text',
      title: 'New folder',
      inputLabel: 'Folder name',
      confirmLabel: 'Create folder',
      onConfirm: async (title) => {
        const folder = await requestJson('/api/vocabulary/folders', {
          method: 'POST',
          body: JSON.stringify({ title }),
        });
        await loadFolders();
        setSelectedFolderId(folder._id);
      },
    });
  };

  const renameFolder = () => {
    if (!selectedFolder) return;
    setActionDialog({
      kind: 'text',
      title: 'Rename folder',
      inputLabel: 'Folder name',
      initialValue: selectedFolder.title,
      confirmLabel: 'Save changes',
      onConfirm: async (title) => {
        if (title === selectedFolder.title) return;
        await requestJson(`/api/vocabulary/folders/${selectedFolder._id}`, {
          method: 'PATCH',
          body: JSON.stringify({ title }),
        });
        await loadFolders();
      },
    });
  };

  const archiveFolder = () => {
    if (!selectedFolder) return;
    setActionDialog({
      kind: 'confirm',
      title: 'Archive folder',
      description: 'Archive this folder? Lists inside it will be hidden.',
      confirmLabel: 'Archive',
      destructive: true,
      onConfirm: async () => {
        await requestJson(`/api/vocabulary/folders/${selectedFolder._id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'archived' }),
        });
        await loadFolders({ preserveSelection: false });
      },
    });
  };

  const createList = async ({ folderId, listTitle, entries }) => {
    const document = await requestJson(`/api/vocabulary/folders/${folderId}/lists`, {
      method: 'POST',
      body: JSON.stringify({ title: listTitle, entries }),
    });
    setPicker(null);
    setSelectedFolderId(folderId);
    await loadFolders();
    await loadLists(folderId, document._id);
    setSelectedListId(document._id);
  };

  const addEntries = async ({ entries }) => {
    if (!selectedListId || entries.length === 0) {
      setPicker(null);
      return;
    }
    const payload = await requestJson(`/api/vocabulary/lists/${selectedListId}/entries`, {
      method: 'POST',
      body: JSON.stringify({ entries }),
    });
    setPicker(null);
    setListDetails(payload.list);
    await loadLists(selectedFolderId, selectedListId);
  };

  const renameList = () => {
    if (!listDetails) return;
    setActionDialog({
      kind: 'text',
      title: 'Rename list',
      inputLabel: 'List name',
      initialValue: listDetails.title,
      confirmLabel: 'Save changes',
      onConfirm: async (title) => {
        if (title === listDetails.title) return;
        const updated = await requestJson(`/api/vocabulary/lists/${listDetails._id}`, {
          method: 'PUT',
          body: JSON.stringify({ title }),
        });
        setListDetails((current) => ({ ...current, title: updated.title }));
        await loadLists(selectedFolderId, selectedListId);
      },
    });
  };

  const moveList = async (folderId) => {
    if (!listDetails || !folderId || folderId === selectedFolderId) return;
    try {
      await requestJson(`/api/vocabulary/lists/${listDetails._id}`, {
        method: 'PUT',
        body: JSON.stringify({ folder_id: folderId }),
      });
      setSelectedFolderId(folderId);
      await loadFolders();
      await loadLists(folderId, listDetails._id);
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const archiveList = () => {
    if (!listDetails) return;
    setActionDialog({
      kind: 'confirm',
      title: 'Archive list',
      description: 'Archive this list?',
      confirmLabel: 'Archive',
      destructive: true,
      onConfirm: async () => {
        await requestJson(`/api/vocabulary/lists/${listDetails._id}`, { method: 'DELETE' });
        await loadFolders();
        await loadLists(selectedFolderId);
      },
    });
  };

  const toggleLearningSource = async (listId) => {
    const current = settings.enabled_personal_list_ids || [];
    const enabled = current.includes(listId)
      ? current.filter((value) => value !== listId)
      : [...current, listId];
    await saveSettings({ enabled_personal_list_ids: enabled });
  };

  return (
    <main className="min-h-full bg-gray-50 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-blue-700">{t('Personal vocabulary')}</p>
            <h1 className="text-3xl font-bold text-gray-950">{t('My Lists')}</h1>
            <p className="mt-1 text-gray-600">{t('Save exact meanings, organize them, and choose which lists support your learning.')}</p>
          </div>
          <button type="button" onClick={loadWorkspace} className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-100">
            <ArrowPathIcon className="h-5 w-5" /> {t('Refresh')}
          </button>
        </header>

        {error && <p role="alert" className="mb-4 border-l-4 border-red-500 bg-red-50 p-3 text-red-800">{error}</p>}

        <div className="grid min-h-[620px] overflow-hidden rounded-lg border border-gray-200 bg-white lg:grid-cols-[240px_320px_minmax(0,1fr)]">
          <aside className="border-b bg-gray-50 p-4 lg:border-b-0 lg:border-r">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-bold text-gray-950">{t('Folders')}</h2>
              <button type="button" onClick={createFolder} title={t('New folder')} className="p-2 text-blue-700 hover:bg-blue-50"><FolderPlusIcon className="h-5 w-5" /></button>
            </div>
            {loading ? <p className="text-sm text-gray-500">{t('Loading...')}</p> : (
              <nav className="space-y-1">
                {folders.map((folder) => (
                  <button key={folder._id} type="button" onClick={() => { setSelectedFolderId(folder._id); setListPage(1); setListSearch(''); }} className={`flex w-full items-center gap-2 rounded px-3 py-2.5 text-left ${folder._id === selectedFolderId ? 'bg-blue-100 text-blue-900' : 'text-gray-700 hover:bg-gray-100'}`}>
                    <FolderIcon className="h-5 w-5 shrink-0" />
                    <span data-i18n-skip className="min-w-0 flex-1 truncate font-medium">{folder.title}</span>
                    <span className="text-xs text-gray-500">{folder.list_count || 0}</span>
                  </button>
                ))}
              </nav>
            )}
            {selectedFolder && selectedFolder._system_key !== 'personal-default' && (
              <div className="mt-4 flex gap-2 border-t pt-4">
                <button type="button" onClick={renameFolder} title={t('Rename folder')} className="p-2 text-gray-600 hover:bg-gray-100"><PencilSquareIcon className="h-5 w-5" /></button>
                <button type="button" onClick={archiveFolder} title={t('Archive folder')} className="p-2 text-red-600 hover:bg-red-50"><ArchiveBoxIcon className="h-5 w-5" /></button>
              </div>
            )}
          </aside>

          <section className="border-b p-4 lg:border-b-0 lg:border-r">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <h2 data-i18n-skip className="font-bold text-gray-950">{selectedFolder?.title || t('Lists')}</h2>
                <p className="text-xs text-gray-500">{t('{count} lists', { count: listPagination.total })}</p>
              </div>
              <button type="button" onClick={() => setPicker('create')} disabled={!selectedFolderId} className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                <PlusIcon className="h-4 w-4" /> {t('New list')}
              </button>
            </div>
            <label className="mb-3 block text-sm font-medium text-gray-800">
              {t('Search teaching lists')}
              <input
                type="search"
                value={listSearch}
                onChange={(event) => { setListSearch(event.target.value); setListPage(1); }}
                className="mt-1 w-full rounded border border-gray-300 p-2.5"
                placeholder={t('Search by list name')}
              />
            </label>
            <div className="space-y-2">
              {lists.map((list) => (
                <button key={list._id} type="button" onClick={() => setSelectedListId(list._id)} className={`w-full rounded border p-3 text-left ${list._id === selectedListId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="flex items-center gap-2">
                    {list.is_favorites && <HeartIcon className="h-5 w-5 fill-rose-500 text-rose-500" />}
                    <strong data-i18n-skip className="min-w-0 flex-1 truncate text-gray-950">{list.title}</strong>
                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{t('{count} meanings', { count: list.entry_count || 0 })}</p>
                </button>
              ))}
              {!lists.length && !loading && <p className="py-10 text-center text-sm text-gray-500">{listSearch ? t('No lists match your search.') : t('No lists in this folder yet.')}</p>}
            </div>
            {listPagination.pages > 1 && (
              <div className="mt-3 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setListPage((current) => Math.max(1, current - 1))}
                  disabled={listPagination.page <= 1 || loading}
                  className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                  title={t('Previous page')}
                >
                  <ChevronLeftIcon className="h-4 w-4" />{t('Previous')}
                </button>
                <span className="text-xs text-gray-500">{t('Page {page} of {pages}', { page: listPagination.page, pages: listPagination.pages })}</span>
                <button
                  type="button"
                  onClick={() => setListPage((current) => Math.min(listPagination.pages, current + 1))}
                  disabled={listPagination.page >= listPagination.pages || loading}
                  className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                  title={t('Next page')}
                >
                  {t('Next')}<ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </section>

          <section className="min-w-0 p-4 sm:p-5">
            {detailLoading ? <p className="text-gray-500">{t('Loading...')}</p> : listDetails ? (
              <>
                <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      {listDetails.is_favorites && <HeartIcon className="h-6 w-6 fill-rose-500 text-rose-500" />}
                      <h2 data-i18n-skip className="text-2xl font-bold text-gray-950">{listDetails.title}</h2>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{t('{count} exact meanings', { count: listDetails.entry_count || 0 })}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setPicker('add')} className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"><PlusIcon className="h-4 w-4" /> {t('Add meanings')}</button>
                    <button type="button" onClick={renameList} title={t('Rename list')} className="rounded border border-gray-300 p-2 text-gray-700 hover:bg-gray-100"><PencilSquareIcon className="h-5 w-5" /></button>
                    {!listDetails.is_favorites && <button type="button" onClick={archiveList} title={t('Archive list')} className="rounded border border-red-200 p-2 text-red-600 hover:bg-red-50"><ArchiveBoxIcon className="h-5 w-5" /></button>}
                  </div>
                </div>

                <div className="grid gap-3 border-b py-4 sm:grid-cols-2">
                  <label className="rounded border border-gray-200 p-3">
                    <span className="block text-sm font-semibold text-gray-900">{t('Quick-save destination')}</span>
                    <span className="mt-1 block text-xs text-gray-500">{t('New words you save quickly will go to this list.')}</span>
                    <input type="radio" name="capture-list" checked={settings.default_capture_list_id === listDetails._id} onChange={() => saveSettings({ default_capture_list_id: listDetails._id })} className="mt-3 h-4 w-4 text-blue-600" />
                  </label>
                  <label className="rounded border border-gray-200 p-3">
                    <span className="block text-sm font-semibold text-gray-900">{t('Use for personal practice')}</span>
                    <span className="mt-1 block text-xs text-gray-500">{t('Let this list contribute vocabulary to your future practice.')}</span>
                    <input type="checkbox" checked={(settings.enabled_personal_list_ids || []).includes(listDetails._id)} onChange={() => toggleLearningSource(listDetails._id)} className="mt-3 h-4 w-4 text-blue-600" />
                  </label>
                </div>

                <div className="py-4">
                  <label className="mb-4 block max-w-xs text-sm font-medium text-gray-800">
                    {t('Move to folder')}
                    <select value={selectedFolderId} onChange={(event) => moveList(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white p-2">
                      {folders.map((folder) => <option data-i18n-skip key={folder._id} value={folder._id}>{folder.title}</option>)}
                    </select>
                  </label>
                  <div className="overflow-hidden rounded border border-gray-200">
                    {(listDetails.entries || []).map((entry, index) => (
                      <div key={`${entry.word}:${entry.sense_id}:${index}`} className="grid gap-2 border-b p-3 last:border-b-0 sm:grid-cols-[minmax(120px,0.7fr)_minmax(220px,1.5fr)_auto] sm:items-center">
                        <div>
                          <strong data-i18n-skip className="text-gray-950">{entry.word}</strong>
                          {entry.pos && <span data-i18n-skip className="ml-2 text-xs text-gray-500">{entry.pos}</span>}
                        </div>
                        <div>
                          <p data-i18n-skip className="text-sm text-gray-900">{entry.definition_cn || entry.definition_en}</p>
                          {entry.definition_en && <p data-i18n-skip className="mt-0.5 text-xs text-gray-500">{entry.definition_en}</p>}
                        </div>
                        <span className={`w-fit rounded px-2 py-1 text-xs font-semibold ${stateStyles[entry.learning_state] || stateStyles.not_started}`}>
                          {t(stateLabels[entry.learning_state] || 'Not started')}
                        </span>
                      </div>
                    ))}
                    {!listDetails.entries?.length && <p className="p-8 text-center text-sm text-gray-500">{t('This list is empty. Add the first meaning when you are ready.')}</p>}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex min-h-[420px] items-center justify-center text-center">
                <div>
                  <CheckCircleIcon className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-3 font-semibold text-gray-800">{t('Choose a list to see its meanings.')}</p>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <VocabularySensePickerModal
        key={`${picker || 'closed'}:${selectedFolderId}`}
        isOpen={Boolean(picker)}
        onClose={() => setPicker(null)}
        folders={folders}
        initialFolderId={selectedFolderId}
        requireListDetails={picker === 'create'}
        heading={picker === 'create' ? 'Create a vocabulary list' : 'Add meanings to this list'}
        onSubmit={picker === 'create' ? createList : addEntries}
      />
      <VocabularyActionDialog dialog={actionDialog} onClose={() => setActionDialog(null)} />
    </main>
  );
};

export default StudentWordbooksPage;
